import { FC, useContext, useEffect, useReducer } from "react"
import { useQueryClient } from "react-query";
import usePools from "../../hooks/pool/usePools";
import { PoolProgressProps } from "../../interfaces";
import { SET_ACTIVE_POOL, ADD_POOLS, UPDATE_POOL_CONTRIBUTION, UPDATE_USER_PLEDGE, UNSET_ACTIVE_POOL, SET_LIVE_POOLS, SET_CLOSED_POOLS, UPDATE_LIVE_POOLS, UPDATE_CLOSED_POOLS, ADD_USER_PLEDGE } from "../action";
import AuthContext from "../Auth/AuthContext";
import PoolContext from "./PoolContext";
import { applyToPartnerWhitelistFunction, applyToPoolWhitelistFunction, filterPoolByStatus, getUserPledges } from "./PoolFunction";
import PoolReducer from "./PoolReducer"
import usePoolsWithStatus from "../../hooks/pool/usePoolsNew";
import { validateUser, validateUserWhitelist } from "../../helpers/validateUser";
import { appConfig } from "../../config";

// Init state should check local storage
const initState = {
    pools: [],
    livePools: [],
    closedPools: [],
    activePool: null,
    poolProgress: [],
    userPledges: [],
}

const PoolState: FC = (props) => {
    
    const { isAuth }= useContext<any>(AuthContext);
    const [ state, dispatch ] = useReducer(PoolReducer, initState);
    const queryClient = useQueryClient();

    const { pools: livePools, fetchNext: fetchMoreLivePools, count: totalLivePosts, isLoading: isLivePoolsLoading, error: livePoolsError, page: livePoolsPageNumber } = usePoolsWithStatus(['ongoing', 'whitelist_closed', 'whitelist_open', 'upcoming'], 3);
    
    const { pools: closedPools, paginatedResult: closedPoolsPaginated, fetchNext: fetchMoreClosedPools, count: totalClosedPools, isLoading: isClosedPoolsLoading, error: closedPoolsError, page: closedPoolsPageNumber, fetchPage } = usePoolsWithStatus(['closed'], 10, isAuth ? 'my' : 'all');

    useEffect(() => {
        if (isAuth) getUserListPledge();
    }, [isAuth])

    useEffect(() => {
        dispatch({
            type: SET_LIVE_POOLS,
            value: livePools
        })
    }, [livePools])

    useEffect(() => {
        dispatch({
            type: SET_CLOSED_POOLS,
            value: closedPools,
        })
    }, [closedPools])

    const addPools = (pools: any[]) => {
        dispatch({
            type: ADD_POOLS,
            value: pools
        })
    }

    const getPoolProgressDetails = (poolId: string) => {
        const poolProgress = state.poolProgress.filter((poolProgress: PoolProgressProps) => poolProgress.poolId === poolId);
        if (poolProgress.length > 0) {
            return poolProgress[0].progress;
        } else {
            return false;
        }
    }

    const setPoolProgressDetails = (poolId: string, cap: number, amount: number) => {
        const progressExists = state.poolProgress.filter((poolProgress: PoolProgressProps) => poolProgress.poolId === poolId && poolProgress.progress.cap === cap && poolProgress.progress.amount === amount).length > 0;
        if (progressExists) return;
        else {
            dispatch({
                type: UPDATE_POOL_CONTRIBUTION,
                value: { poolId: poolId, cap: cap, amount: amount },
            });
        }
    }
    
    const refetchPoolData = () => {
        queryClient.invalidateQueries({ queryKey: ['pools']});
    }

    const getUserListPledge = async () => {
        getUserPledges()
        .then((data: any) => {
            dispatch({
                type: UPDATE_USER_PLEDGE,
                value: data.listUserPledges,
            })
        })
        .catch((error: any) => {
            // TODO: Handle error with a separate hook for error
            // state.
            console.log(error);
        })
    }

    const getPoolPledges = async (poolId: string) => {
        return state.userPledges.filter((pledge: any) => pledge.poolId === poolId);
    }

    const getPoolById = (id: string) => {
        return state.livePools.concat(state.closedPools).filter((pool: { id: string }) => pool.id === id)
    }

    // TODO: Add pool interface
    const setActivePool = (pool: { id: string, networks: any[], name: string, slug: string }) => {
        dispatch({
            type: SET_ACTIVE_POOL,
            value: pool,
        })
    }

    const unsetActivePool = (pool: any) => {
        dispatch({
            type: UNSET_ACTIVE_POOL,
        })
    }

    const getPledgeData = () => {

    }

    const getActivePool = () => {
        if (state.activePool) {
            return state.activePool;
        }
        const activePool = localStorage.getItem("activePool")!;
        if (activePool) {
            return JSON.parse(activePool);
        } else {
            return {};
        }
    }

    const getLivePools = () => {
        return filterPoolByStatus(state.livePools, ['ongoing']);
    }

    const getNextPools = () => {
        return filterPoolByStatus(state.livePools, ['whitelist_open', 'whitelist_closed']);
    }

    const getUpcomingPools = () => {
        return filterPoolByStatus(state.livePools, ['upcoming']);
    }

    const getPastPools = () => {
        return state.closedPools;
    }

    // TODO: Rename the variables
    // and functions and 
    // updated pools container to use
    // these

    const getWhitelistedByStatus = (status: string) => {
        return status === "whitelist_open" ||
        status === "whitelist_closed" ||
        status === "upcoming"
    }

    const getInvestBtnByPool = (poolId: string) => {
        return validateUser(poolId, state.userPledges)
    }

    const getWhitelistedCompletedByPool = (poolId: string) => {
        return validateUserWhitelist(poolId, state.userPledges)
    }

    const addPoolToLivePools = (pool: any) => {
        dispatch({
            type: UPDATE_LIVE_POOLS,
            data: pool,
        })
    }

    const addPoolToClosedPools = (pool: any) => {
        dispatch({
            type: UPDATE_CLOSED_POOLS,
            data: pool,
        })
    }

    const isGiveaway = (id: string) => {
        return appConfig.giveAwayProjectId.includes(id);
    }

    const applyToPartnerWhitelist = async (poolId: string, code: string) => {
        return new Promise((resolve, reject) => {
            applyToPartnerWhitelistFunction(poolId, code)
            .then((data: any) => {
                dispatch({
                    type: ADD_USER_PLEDGE,
                    value: data.applyToPartnerWhitelist
                })
                resolve(data);
            })
            .catch((error: any) => {
                reject(error);
            })
        })
    }

    return (
        <PoolContext.Provider
            value={{
                pools: state.pools,
                userPledges: state.userPledges,
                poolProgress: state.poolProgress,
                livePools: state.livePools,
                closedPools: state.closedPools,
                closedPoolsPaginated,
                livePoolsPageNumber,
                closedPoolsPageNumber,
                isLivePoolsLoading,
                isClosedPoolsLoading,
                livePoolsError,
                closedPoolsError,
                totalLivePosts,
                totalClosedPools,
                getPoolProgressDetails,
                setPoolProgressDetails,
                getPoolPledges,
                refetchPoolData,
                getPoolById,
                setActivePool,
                unsetActivePool,
                getPledgeData,
                getActivePool,
                getLivePools,
                getNextPools,
                getUpcomingPools,
                getPastPools,
                fetchMoreLivePools,
                fetchMoreClosedPools,
                fetchPage,
                getUserListPledge,
                getInvestBtnByPool,
                getWhitelistedCompletedByPool,
                getWhitelistedByStatus,
                addPoolToLivePools,
                addPoolToClosedPools,
                isGiveaway,
                applyToPartnerWhitelist,
            }}
        >
            { props.children }
        </PoolContext.Provider>
    );
}

export default PoolState;